import { type ModalityType, type ApiToolCallModel } from '@littlebird/sdk';
import { motion } from 'motion/react';
import { type HtmlHTMLAttributes, type ReactNode, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { XStack } from '@/components/ui';
import { EASE } from '@/lib/animate';
import { cn } from '@/lib/utils';

import {
  ReasoningStepContextProvider,
  useReasoningStepContext,
} from './reasoning-step.context';

type RootProps = {
  children: ReactNode;
  last: boolean;
  isNew: boolean;
  isActive?: boolean;
  isLiveTodo?: boolean;
  defaultOpen?: boolean;
  canExpand?: boolean;
  callCount?: number;
  toolCall?: ApiToolCallModel;
  stepId?: string;
  // E2E hook: modality type of this step; undefined for Thought steps.
  dataModalityType?: ModalityType;
};

export const Root = ({
  children,
  last,
  isNew = false,
  isActive = false,
  isLiveTodo = false,
  defaultOpen = false,
  canExpand = true,
  callCount = 1,
  toolCall,
  stepId,
  dataModalityType,
}: RootProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <ReasoningStepContextProvider
      value={{
        isOpen,
        setIsOpen,
        isHovered,
        setIsHovered,
        last,
        isNew,
        isActive,
        isLiveTodo,
        canExpand,
        callCount,
        toolCall,
        stepId,
      }}
    >
      <motion.div
        className="flex flex-col"
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{
          opacity: 0,
          y: -4,
          transition: { duration: 0.12, ease: EASE.outQuint },
        }}
        transition={{ duration: 0.22, ease: EASE.outQuint }}
        aria-expanded={canExpand ? isOpen : undefined}
        onPointerEnter={(event) => {
          // hover is a mouse interaction; a touch tap must not swap the
          // leading icon for the chevron or WebKit withholds the click
          if (event.pointerType !== 'mouse') return;
          setIsHovered(true);
        }}
        onPointerLeave={(event) => {
          if (event.pointerType !== 'mouse') return;
          setIsHovered(false);
        }}
        data-testid="reasoning-step"
        data-modality-type={dataModalityType}
      >
        {children}
      </motion.div>
    </ReasoningStepContextProvider>
  );
};

export const Preview = ({ children }: HtmlHTMLAttributes<HTMLDivElement>) => {
  const { setIsOpen, isOpen, canExpand } = useReasoningStepContext();

  const handleToggle = () => {
    if (!canExpand) return;
    setIsOpen(!isOpen);
  };

  return (
    <XStack
      className="items-center w-full gap-3"
      onClick={handleToggle}
      onKeyDown={(e) => {
        if (!canExpand) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleToggle();
        }
      }}
      tabIndex={canExpand ? 0 : undefined}
    >
      {children}
    </XStack>
  );
};

export const PreviewIcon = ({
  children,
}: HtmlHTMLAttributes<HTMLDivElement>) => {
  const { isOpen, isHovered, canExpand } = useReasoningStepContext();

  return (
    <div className="shrink-0 flex items-center justify-center size-3.5 text-foreground-muted">
      {canExpand && isHovered ? (
        <Icons.chevronDown
          className={cn(
            'size-3.5 transition-transform motion-reduce:transition-none',
            isOpen && 'rotate-180'
          )}
        />
      ) : (
        children
      )}
    </div>
  );
};

export const CallCountSuffix = ({ count }: { count: number }) => {
  if (count < 2) return null;
  return (
    <>
      <span
        aria-hidden="true"
        className="inline-flex h-4 min-w-4 items-center justify-center rounded-sm bg-surface-active px-1 text-xs leading-none text-[var(--reasoning-accent)] dark:bg-surface-hover"
      >
        {count}
      </span>
      <span className="sr-only">{count} calls</span>
    </>
  );
};

export const PreviewText = ({
  children,
  title,
}: HtmlHTMLAttributes<HTMLDivElement>) => {
  const { isNew, callCount } = useReasoningStepContext();
  return (
    <span
      className={cn(
        'relative flex min-w-0 items-center gap-1.5 text-sm text-foreground-subtle select-none',
        isNew && [
          'bg-[linear-gradient(to_right,var(--gray-11)_0%,var(--gray-11)_67.5%,var(--gray-12)_72.5%,var(--gray-12)_87.5%,var(--gray-11)_92.5%,var(--gray-11)_100%)]',
          'bg-[length:200%_100%]',
          'bg-clip-text text-transparent',
          '[background-position:0%_0]',
          'animate-shimmer-modality',
          'motion-reduce:animate-none',
        ]
      )}
    >
      <span className="truncate" title={title}>
        {children}
      </span>
      <CallCountSuffix count={callCount} />
    </span>
  );
};

export const Content = ({ children }: HtmlHTMLAttributes<HTMLDivElement>) => {
  const { isOpen, last, canExpand } = useReasoningStepContext();
  const isEffectivelyOpen = canExpand && isOpen;

  if (!isEffectivelyOpen && last) return null;

  return (
    <div
      className={cn(
        'pl-5 ml-1.5 my-1.5 min-h-2',
        !last &&
          'border-l border-border dark:border-border-strong group-data-[compact=true]/reasoning:border-grayA-4'
      )}
    >
      {canExpand && (
        <div
          className={cn(
            'grid transition-[grid-template-rows,opacity] duration-200 ease-out-quint',
            'motion-reduce:transition-none',
            'text-foreground-muted text-sm',
            isEffectivelyOpen
              ? 'grid-rows-[1fr] opacity-100'
              : 'grid-rows-[0fr] opacity-0'
          )}
        >
          <div className="overflow-hidden">
            <div
              className={cn(
                isEffectivelyOpen && 'pt-1',
                isEffectivelyOpen && !last && 'pb-4'
              )}
            >
              {children}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

type ChipContainerProps = {
  children: ReactNode;
};

const chipContainerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.05,
    },
  },
};

export const ChipContainer = ({ children }: ChipContainerProps) => {
  return (
    <motion.div
      className="flex flex-wrap gap-2"
      initial="hidden"
      animate="visible"
      variants={chipContainerVariants}
    >
      {children}
    </motion.div>
  );
};

type ChipProps = {
  children: ReactNode;
  onClick?: () => void;
  className?: string;
};

const chipVariants = {
  hidden: { opacity: 0, y: 4 },
  visible: { opacity: 1, y: 0 },
};

export const Chip = ({ children, onClick, className }: ChipProps) => {
  return (
    <motion.div
      variants={chipVariants}
      transition={{ duration: 0.2, ease: EASE.outQuint }}
      onClick={onClick}
      data-testid="reasoning-chip"
      className={cn(
        'flex items-center gap-2 px-2.5 pr-3 py-1.5 rounded-lg bg-grayA-2 border border-grayA-3 text-xs text-foreground max-w-full min-w-0 break-all select-none',
        'transition-colors duration-150',
        onClick && 'cursor-pointer hover:bg-grayA-3 active:bg-grayA-4',
        className
      )}
    >
      {children}
    </motion.div>
  );
};
